import { io, Socket } from 'socket.io-client';
import { API_BASE_URL } from '../config';
import {
    prepareNxExecutionBridgeRequest,
    ragChatRequiresExecutionApproval,
    declinedNxExecutionMessage,
    parseNxExecutionApprovalKind,
} from '../utils/nxCodeUtils';
import { authService } from './auth.service';
import { nxWebSocketService } from './nxWebSocketService';
import { ackDesktopSkillCatalogScan } from '../utils/electronSkills';
import { ackDesktopMcpRequest } from '../utils/electronMcp';

/**
 * Editable plan payload sent by the NX agent when plan mode is enabled.
 * The plan is a single markdown string shown in an editable textarea on the client.
 */
export interface NxAgentPlanData {
    planId: string;
    name?: string;
    overview?: string;
    plan: string;
}

/**
 * A single option for a question presented by the NX agent.
 */
export interface NxAgentQuestionOption {
    id: string;
    label: string;
}

/**
 * A single question presented by the NX agent. When allow_multiple is true the
 * user may select more than one option (multi-select); otherwise it is single-select.
 */
export interface NxAgentQuestionItem {
    id: string;
    prompt: string;
    allow_multiple?: boolean;
    options: NxAgentQuestionOption[];
}

/**
 * Structured multiple-choice questions sent by the NX agent (ask_user_question tool).
 * The client renders a question card and resolves the ack with a human-readable
 * answer string summarizing the user's selections.
 */
export interface NxAgentQuestionData {
    title?: string;
    questions: NxAgentQuestionItem[];
}

/**
 * Payload for a chat confirmation before NX code, NX deletion, or an MCP tool runs.
 * MCP requests use nx-tool-approval and do not include jaCode.
 */
export interface NxCodeExecutionApprovalRequest {
    jaCode?: string;
    requireApproval?: boolean;
    /** Conversation-level allow is tracked separately for each kind. */
    approvalKind?: 'execute' | 'delete' | 'mcp';
    /** MCP tool name used to attach the prompt to the matching tool card. */
    toolName?: string;
    /** Short argument preview when the live tool card is not available yet. */
    summary?: string;
}

// ===========================
// PROGRESS TRACKING TYPES
// ===========================

/**
 * OCR Job Progress structure
 * Matches server-side OcrJob.progress from ocrQueueService.ts
 */
export interface OcrJobProgress {
    totalPages: number;
    processedPages: number;
    currentPage?: number;
    percentage: number;
}

/**
 * Progress update data from backend WebSocketProgressService
 */
export interface ProgressUpdateData {
    taskId: string;
    progress: number;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    currentStep?: string;
    currentFile?: string;
    processedFiles?: number;
    totalFiles?: number;
    details?: {
        uploadProgress?: number;
        processingProgress?: number;
        embeddingProgress?: number;
        vectorProgress?: number;
        error?: string;
    };
}

/**
 * OCR job status data from backend
 */
export interface OcrJobStatusData {
    type: 'job_status';
    data: {
        jobId: string;
        uploadJobId?: string;  // Parent upload job ID for matching
        status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
        progress: OcrJobProgress;
        message: string;
        timestamp: Date;
    };
}

/**
 * OCR job progress data from backend
 */
export interface OcrJobProgressData {
    type: 'job_progress';
    data: {
        jobId: string;
        uploadJobId?: string;  // Parent upload job ID for matching
        progress: OcrJobProgress;
        currentPage?: number;
        results: number;
        timestamp: Date;
    };
}

/**
 * NX Training embedding job progress, pushed by the server background job.
 * Mirrors NXEmbeddingJobStatus on the server.
 */
export interface NxEmbeddingProgressData {
    companyId: string;
    state: 'running' | 'completed' | 'failed';
    phase?: 'starting' | 'generating' | 'storing' | 'done';
    processed: number;
    total: number;
    successCount: number;
    errorCount: number;
    message: string;
    errorKey?: string;
    params?: Record<string, any>;
    errors?: string[];
    startedAt: number;
    finishedAt?: number;
}

const NX_SNIPPET_REGISTRATION_ACK_TIMEOUT_MS = 5_000;
const NX_SNIPPET_REGISTRATION_ATTEMPTS = 2;

const STABLE_CLIENT_ID_KEY = 'aiee-ws-client-id';

/**
 * Stable per-tab client ID sent in the Socket.IO handshake query.
 *
 * The server keys everything it routes back to this tab on the handshake
 * `clientId` (agent stream events, NX code execution, background job progress),
 * falling back to `socket.id`. Because `socket.id` changes on every reconnect,
 * a mid-run reconnect used to orphan the running agent: its captured clientId
 * no longer resolved to a live socket, so `stream_end` and the NX execution
 * result were silently dropped and the UI stayed on "executing" forever.
 *
 * sessionStorage (not localStorage) keeps each tab a distinct client, matching
 * the one-NX-connection-per-tab model.
 */
const resolveStableClientId = (): string => {
    const generate = () => `web_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    try {
        const existing = sessionStorage.getItem(STABLE_CLIENT_ID_KEY);
        if (existing) return existing;
        const created = generate();
        sessionStorage.setItem(STABLE_CLIENT_ID_KEY, created);
        return created;
    } catch {
        // sessionStorage unavailable (privacy mode) — fall back to a per-load id.
        return generate();
    }
};

interface NxSnippetRegistrationResponse {
    ok?: boolean;
    sessionId?: string;
    companyId?: string;
    serverIdentity?: {
        hostname: string;
        pid: number;
        instanceId: string | null;
        bootId: string;
    };
    error?: string;
}

/**
 * WebSocket Service for real-time communication with the server
 * Handles voice messages and RAG chat via WebSocket
 */
class WebSocketService {
    private socket: Socket | null = null;
    private isConnected = false;
    /** Survives reconnects so the server keeps routing this tab's agent run correctly. */
    private readonly stableClientId = resolveStableClientId();
    private nxSnippetRouteReady = false;
    private nxSnippetSessionId: string | null = null;
    /** socket.id the current registration was acknowledged for (registration is per-socket). */
    private nxSnippetRegisteredSocketId: string | null = null;
    private nxSnippetRegistrationPromise: Promise<boolean> | null = null;
    private nxAgentStreamHandlers: Array<(payload: { role: string; content: string; node?: string; type?: string; toolName?: string; toolId?: string; toolStatus?: 'success' | 'error'; aborted?: boolean; conversationId?: string; classificationData?: any; sources?: any[]; projectTableData?: any }) => void> = [];
    private ragStreamHandlers: Array<(payload: any) => void> = [];
    // Single handler that renders the editable plan card and resolves with the user-confirmed plan text.
    private nxAgentPlanHandler: ((plan: NxAgentPlanData) => Promise<string>) | null = null;
    // Single handler that renders the question card and resolves with the user's answer string.
    private nxAgentQuestionHandler: ((question: NxAgentQuestionData) => Promise<string>) | null = null;
    // Single handler that asks the user to confirm execute_python_code before NX runs it.
    private nxCodeExecutionApprovalHandler: ((request: NxCodeExecutionApprovalRequest) => Promise<boolean>) | null = null;
    private pendingCodeApprovalCancel: (() => void) | null = null;

    // Progress tracking handlers
    private progressHandlers: Set<(data: ProgressUpdateData) => void> = new Set();
    private ocrJobStatusHandlers: Set<(data: OcrJobStatusData) => void> = new Set();
    private ocrJobProgressHandlers: Set<(data: OcrJobProgressData) => void> = new Set();
    private nxEmbeddingHandlers: Set<(data: NxEmbeddingProgressData) => void> = new Set();

    private resetNxSnippetRouteState(): void {
        this.nxSnippetRouteReady = false;
        this.nxSnippetSessionId = null;
        this.nxSnippetRegisteredSocketId = null;
        this.nxSnippetRegistrationPromise = null;
    }

    private async ensureNxSnippetSessionRegistered(socket: Socket): Promise<boolean> {
        const socketId = socket.id;
        if (!socket.connected || !socketId) return false;
        if (this.nxSnippetRouteReady && this.nxSnippetRegisteredSocketId === socketId) return true;
        if (this.nxSnippetRegistrationPromise) return this.nxSnippetRegistrationPromise;

        const registration = (async () => {
            for (let attempt = 1; attempt <= NX_SNIPPET_REGISTRATION_ATTEMPTS; attempt += 1) {
                if (this.socket !== socket || !socket.connected || socket.id !== socketId) return false;

                try {
                    const response = await new Promise<NxSnippetRegistrationResponse>((resolve, reject) => {
                        socket.timeout(NX_SNIPPET_REGISTRATION_ACK_TIMEOUT_MS).emit(
                            'nx-snippet-session-register',
                            {},
                            (error: Error | null, ack: NxSnippetRegistrationResponse) => {
                                if (error) reject(error);
                                else resolve(ack);
                            }
                        );
                    });

                    if (this.socket !== socket || !socket.connected || socket.id !== socketId) {
                        console.warn('[NX snippet client] stale session registration acknowledgement ignored', {
                            requestedSocketId: socketId,
                            currentSocketId: this.socket?.id ?? null,
                        });
                        return false;
                    }

                    if (response?.ok) {
                        this.nxSnippetRouteReady = true;
                        this.nxSnippetRegisteredSocketId = socketId;
                        this.nxSnippetSessionId = response.sessionId ?? this.stableClientId;
                        console.info('[NX snippet client] session registration acknowledged', {
                            pageOrigin: window.location.origin,
                            socketId,
                            sessionId: this.nxSnippetSessionId,
                            companyId: response.companyId ?? null,
                            serverIdentity: response.serverIdentity ?? null,
                        });
                        return true;
                    }

                    console.warn('[NX snippet client] session registration rejected', {
                        socketId,
                        attempt,
                        error: response?.error || 'Unknown registration error',
                    });
                } catch (error: any) {
                    console.warn('[NX snippet client] session registration acknowledgement failed', {
                        socketId,
                        attempt,
                        error: error?.message || String(error),
                    });
                }
            }

            this.nxSnippetRouteReady = false;
            this.nxSnippetSessionId = null;
            this.nxSnippetRegisteredSocketId = null;
            return false;
        })();

        this.nxSnippetRegistrationPromise = registration;
        try {
            return await registration;
        } finally {
            if (this.nxSnippetRegistrationPromise === registration) {
                this.nxSnippetRegistrationPromise = null;
            }
        }
    }

    /**
     * Initialize WebSocket connection
     */
    connect(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            try {
                // 检查是否已经有活跃连接
                if (this.socket && this.isConnected && this.socket.connected) {
                    console.log('✅ WebSocket already connected, verifying NX snippet route:', this.socket.id);
                    void this.ensureNxSnippetSessionRegistered(this.socket).then(resolve);
                    return;
                }

                // 如果有旧连接但未连接，先清理
                if (this.socket) {
                    console.log('🧹 Cleaning up previous WebSocket connection');
                    this.socket.disconnect();
                    this.socket = null;
                    this.isConnected = false;
                    this.resetNxSnippetRouteState();
                }

                // Dynamic WebSocket URL for external access support
                let wsUrl: string;

                if (API_BASE_URL) {
                    // Use the configured API base URL
                    wsUrl = API_BASE_URL;
                } else {
                    // For NGINX proxy setup, use relative URL (current domain)
                    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
                    const host = window.location.host;
                    wsUrl = `${protocol}//${host}`;
                }

                console.log('🔌 Creating new WebSocket connection', {
                    pageOrigin: window.location.origin,
                    apiBaseUrl: API_BASE_URL || '(relative)',
                    socketTarget: wsUrl,
                });

                // Get JWT token for authentication
                const token = authService.getToken();
                console.log('[WebSocket] Including JWT token in connection:', token ? 'Present' : 'Missing');

                this.socket = io(wsUrl, {
                    // Polling-first: connect over HTTP long-polling immediately (works through
                    // any reverse proxy, incl. the CRA dev proxy and nginx), then transparently
                    // upgrade to a raw WebSocket if the environment allows it. Forcing
                    // 'websocket' first makes the client hang for the full `timeout` and fail
                    // with "WebSocket connection failed" whenever the WS upgrade is not proxied
                    // (e.g. nginx missing `proxy_http_version 1.1`).
                    transports: ['polling', 'websocket'],
                    forceNew: false, // Allow connection reuse to reduce disconnections
                    timeout: 60000,  // Initial connection timeout
                    // Keep retrying so MCP routing recovers after backend restarts.
                    reconnectionAttempts: Infinity,
                    reconnectionDelay: 2000,
                    reconnectionDelayMax: 10000,
                    // Match server-side ping/pong configuration
                    // Note: Client automatically adapts to server settings, but explicit config helps
                    autoConnect: true,
                    upgrade: true,
                    rememberUpgrade: true,
                    // Include JWT token for server-side verification
                    auth: {
                        token: token
                    },
                    query: {
                        token: token, // Also include in query as fallback
                        // Server-side routing key. Stable across reconnects so an
                        // in-flight agent run keeps reaching this tab.
                        clientId: this.stableClientId
                    }
                });

                this.socket.on('connect', async () => {
                    const connectedSocket = this.socket;
                    console.log('✅ WebSocket connected:', connectedSocket?.id);
                    this.isConnected = true;
                    if (!connectedSocket) {
                        resolve(false);
                        return;
                    }

                    const registered = await this.ensureNxSnippetSessionRegistered(connectedSocket);
                    resolve(registered);
                });

                this.socket.on('connect_error', (error: Error) => {
                    console.warn('⚠️ WebSocket connection failed - continuing without real-time features:', error.message);
                    this.isConnected = false;
                    this.resetNxSnippetRouteState();
                    // Don't reject - allow app to continue without WebSocket
                    resolve(false);
                });

                this.socket.on('disconnect', (reason: string) => {
                    console.log('🔌 WebSocket disconnected:', reason);
                    this.isConnected = false;
                    this.resetNxSnippetRouteState();
                    this.cancelPendingCodeExecutionApproval();
                    this.clearNxAgentStreamHandlers(reason);
                });

                // Handle voice responses
                this.socket.on('voice-response', (data: any) => {
                    console.log('🎙️ Voice response received:', data);
                });

                this.socket.on('voice-error', (error: any) => {
                    console.error('🎙️ Voice error:', error);
                });

                this.socket.on('desktop-mcp', (event: any, ackFn?: (resp: { ok: boolean; result?: any; error?: any }) => void) => {
                    const safeAck = typeof ackFn === 'function' ? ackFn : () => { };
                    void ackDesktopMcpRequest(event?.data, safeAck);
                });

                // Handle RAG chat responses
                this.socket.on('rag-chat', (event: any, ackFn?: (resp: { ok: boolean; result?: any; error?: any }) => void) => {
                    // Silent trusted journals can contain scoped transfer credentials.
                    console.log('🤖 RAG response received:', event.data?.silent ? { silent: true } : event.data);
                    const safeAck = typeof ackFn === 'function' ? ackFn : () => { };
                    if (event.data && event.data.skillCatalogScan) {
                        void ackDesktopSkillCatalogScan(safeAck);
                        return;
                    }
                    if (event.data && event.data.jaCode) {
                        const approvalKind = parseNxExecutionApprovalKind(event.data.approvalKind);
                        const declinedMessage = declinedNxExecutionMessage(approvalKind);
                        const declinedAck = {
                            ok: false,
                            result: {
                                success: false,
                                message: declinedMessage,
                                data: declinedMessage,
                            },
                        };
                        const runJournal = () => {
                            const bridgeRequest = prepareNxExecutionBridgeRequest(event.data);
                            console.log('🤖 RAG response contains code, executing via nxWebSocketService...');

                            if (bridgeRequest.publishCode) {
                                // Emit custom event to update globalCode in RagChat component
                                window.dispatchEvent(new CustomEvent('updateGlobalCode', {
                                    detail: { code: event.data.jaCode }
                                }));
                            }

                            nxWebSocketService.executeCode(bridgeRequest.codeToExecute).then(result => {
                                console.log('🤖 NX execution result:', result);
                                // Prefer structured success (status === 0); keep ok for sendAndWait.
                                safeAck({ ok: !!result.success, result });
                            }).catch(error => {
                                console.error('🤖 NX execution failed:', error);
                                safeAck({ ok: false, error });
                            });
                        };

                        if (!ragChatRequiresExecutionApproval(event.data) || !this.nxCodeExecutionApprovalHandler) {
                            runJournal();
                            return;
                        }

                        this.awaitCodeExecutionApproval({
                            jaCode: event.data.jaCode,
                            requireApproval: true,
                            approvalKind,
                        }).then((approved) => {
                            if (!approved) {
                                safeAck(declinedAck);
                                return;
                            }
                            runJournal();
                        }).catch((error) => {
                            console.error('[WS] execute_python_code approval failed:', error);
                            safeAck(declinedAck);
                        });
                    }
                });

                // MCP tools do not run NX journals. Ask in chat, then ack so the server
                // either calls the MCP server or returns the decline text.
                this.socket.on('nx-tool-approval', (event: any, ackFn?: (resp: { ok: boolean; result?: any; error?: any }) => void) => {
                    const safeAck = typeof ackFn === 'function' ? ackFn : () => { };
                    const data = event?.data || {};
                    const toolName = typeof data.toolName === 'string' ? data.toolName : undefined;
                    const approvalKind = parseNxExecutionApprovalKind(data.approvalKind);
                    const declinedMessage = declinedNxExecutionMessage(approvalKind, toolName);
                    const ackApproved = () => safeAck({
                        ok: true,
                        result: { success: true, message: 'approved', data: 'approved' },
                    });
                    const ackDeclined = () => safeAck({
                        ok: false,
                        result: { success: false, message: declinedMessage, data: declinedMessage },
                    });
                    if (approvalKind !== 'mcp' && !(approvalKind === 'execute' && ['import_cad_to_nx', 'download_cad_artifact'].includes(toolName || ''))) {
                        ackDeclined();
                        return;
                    }
                    this.awaitCodeExecutionApproval({
                        requireApproval: true,
                        approvalKind,
                        toolName,
                        summary: typeof data.summary === 'string' ? data.summary : undefined,
                    }).then((approved) => {
                        if (approved) ackApproved();
                        else ackDeclined();
                    }).catch((error) => {
                        console.error('[WS] MCP tool approval failed:', error);
                        ackDeclined();
                    });
                });

                this.socket.on('rag-error', (error: any) => {
                    console.error('🤖 RAG error:', error);
                });

                // Streamed AI messages from NX agent
                this.socket.on('nx-agent-stream', (payload: { role: string; content: string; node?: string; type?: string; toolName?: string; toolId?: string; toolStatus?: 'success' | 'error'; aborted?: boolean; conversationId?: string; classificationData?: any; sources?: any[]; projectTableData?: any; finalNode?: string | null; finalNodeContent?: string }) => {
                    try {
                        this.nxAgentStreamHandlers.forEach(h => h(payload));
                    } catch (e) {
                        console.error('[WS] nx-agent-stream handler error:', e);
                    }
                });

                // Editable plan request from NX agent (plan mode).
                // Mirrors the rag-chat ack pattern: server waits for clientResp.result.message (string).
                this.socket.on('nx-agent-plan', (event: any, ackFn?: (resp: { ok: boolean; result?: { message: string }; error?: any }) => void) => {
                    const safeAck = typeof ackFn === 'function' ? ackFn : () => { };
                    const planData = event?.data as NxAgentPlanData;
                    if (!this.nxAgentPlanHandler) {
                        console.warn('[WS] nx-agent-plan received but no handler registered');
                        safeAck({ ok: false, error: 'No plan handler registered on client' });
                        return;
                    }
                    this.nxAgentPlanHandler(planData)
                        .then((confirmedPlanText: string) => {
                            safeAck({ ok: true, result: { message: confirmedPlanText } });
                        })
                        .catch((error: any) => {
                            console.error('[WS] nx-agent-plan handler error:', error);
                            safeAck({ ok: false, error: error?.message || 'Plan handling failed' });
                        });
                });

                // Structured question request from NX agent (ask_user_question tool).
                // Mirrors the rag-chat ack pattern: server waits for clientResp.result.message (string).
                this.socket.on('nx-agent-question', (event: any, ackFn?: (resp: { ok: boolean; result?: { message: string }; error?: any }) => void) => {
                    const safeAck = typeof ackFn === 'function' ? ackFn : () => { };
                    const questionData = event?.data as NxAgentQuestionData;
                    if (!this.nxAgentQuestionHandler) {
                        console.warn('[WS] nx-agent-question received but no handler registered');
                        safeAck({ ok: false, error: 'No question handler registered on client' });
                        return;
                    }
                    this.nxAgentQuestionHandler(questionData)
                        .then((answerText: string) => {
                            safeAck({ ok: true, result: { message: answerText } });
                        })
                        .catch((error: any) => {
                            console.error('[WS] nx-agent-question handler error:', error);
                            safeAck({ ok: false, error: error?.message || 'Question handling failed' });
                        });
                });

                // Handle NX agent errors
                this.socket.on('nx-agent-error', (error: any) => {
                    console.error('🤖 NX Agent error:', error);
                    const errorContent = error?.error || error?.message || 'Unknown error occurred';
                    this.nxAgentStreamHandlers.forEach(h => h({
                        role: 'assistant',
                        content: errorContent,
                        type: 'stream_error'
                    }));
                });

                // Handle NX agent abort confirmations
                this.socket.on('nx-agent-aborted', (data: any) => {
                    console.log('[WebSocket] 🛑 NX Agent abort confirmation received:', data);
                    if (data.success) {
                        console.log('[WebSocket] ✅ Stream aborted successfully on server');
                    } else {
                        console.warn('[WebSocket] ⚠️ Stream abort failed or no active stream found');
                    }
                });

                // Streamed AI messages from Regular RAG
                this.socket.on('rag-stream', (payload: any) => {
                    try {
                        this.ragStreamHandlers.forEach(h => h(payload));
                    } catch (e) {
                        console.error('[WS] rag-stream handler error:', e);
                    }
                });

                // Handle Regular RAG stream errors
                this.socket.on('rag-stream-error', (error: any) => {
                    console.error('🤖 RAG Stream error:', error);
                });

                // Handle Regular RAG stream abort confirmations
                this.socket.on('rag-stream-aborted', (data: any) => {
                    console.log('[WebSocket] 🛑 RAG Stream abort confirmation received:', data);
                    if (data.success) {
                        console.log('[WebSocket] ✅ RAG Stream aborted successfully on server');
                    } else {
                        console.warn('[WebSocket] ⚠️ Stream abort failed on server:', data.message);
                    }
                });

                // Progress tracking events - single listener pattern (consistent with nxAgentStreamHandlers)
                this.socket.on('progress_update', (data: ProgressUpdateData) => {
                    this.progressHandlers.forEach(callback => {
                        try {
                            callback(data);
                        } catch (e) {
                            console.error('[WS] progress_update handler error:', e);
                        }
                    });
                });

                this.socket.on('ocr_job_status', (data: OcrJobStatusData) => {
                    this.ocrJobStatusHandlers.forEach(callback => {
                        try {
                            callback(data);
                        } catch (e) {
                            console.error('[WS] ocr_job_status handler error:', e);
                        }
                    });
                });

                this.socket.on('ocr_job_progress', (data: OcrJobProgressData) => {
                    this.ocrJobProgressHandlers.forEach(callback => {
                        try {
                            callback(data);
                        } catch (e) {
                            console.error('[WS] ocr_job_progress handler error:', e);
                        }
                    });
                });

                // NX Training background embedding progress
                this.socket.on('nx_embedding_progress', (data: NxEmbeddingProgressData) => {
                    this.nxEmbeddingHandlers.forEach(callback => {
                        try {
                            callback(data);
                        } catch (e) {
                            console.error('[WS] nx_embedding_progress handler error:', e);
                        }
                    });
                });

            } catch (error) {
                console.error('WebSocket initialization error:', error);
                reject(error);
            }
        });
    }

    /**
     * Disconnect WebSocket
     */
    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
            this.isConnected = false;
            this.resetNxSnippetRouteState();
            console.log('🔌 WebSocket disconnected');
        }
    }

    /**
     * Get the client ID the server routes this tab's traffic on.
     * Must match the handshake `clientId` (not socket.id), otherwise callers that
     * pass it to the API — skill install, reuse library, background job progress —
     * would address a connection the server does not have.
     */
    getClientId(): string | null {
        return this.socket?.connected ? this.stableClientId : null;
    }

    /**
     * Send voice message via WebSocket
     */
    sendVoiceMessage(audioData: Blob | ArrayBuffer): Promise<any> {
        return new Promise((resolve, reject) => {
            if (!this.socket || !this.isConnected) {
                reject(new Error('WebSocket not connected'));
                return;
            }

            // Set up response handlers
            const onResponse = (data: any) => {
                this.socket?.off('voice-response', onResponse);
                this.socket?.off('voice-error', onError);
                resolve(data);
            };

            const onError = (error: any) => {
                this.socket?.off('voice-response', onResponse);
                this.socket?.off('voice-error', onError);
                reject(error);
            };

            this.socket.on('voice-response', onResponse);
            this.socket.on('voice-error', onError);

            // Send voice message
            this.socket.emit('voice-message', {
                audio: audioData,
                timestamp: new Date().toISOString()
            });
        });
    }

    /**
     * Send RAG chat message via WebSocket
     */
    sendRagMessage(message: string, conversationId?: string): Promise<any> {
        return new Promise((resolve, reject) => {
            if (!this.socket || !this.isConnected) {
                reject(new Error('WebSocket not connected'));
                return;
            }

            // Set up response handlers
            const onResponse = (data: any) => {
                this.socket?.off('rag-response', onResponse);
                this.socket?.off('rag-error', onError);
                resolve(data);
            };

            const onError = (error: any) => {
                this.socket?.off('rag-response', onResponse);
                this.socket?.off('rag-error', onError);
                reject(error);
            };

            this.socket.on('rag-response', onResponse);
            this.socket.on('rag-error', onError);

            // Send RAG message
            this.socket.emit('rag-chat', {
                message,
                conversationId,
                timestamp: new Date().toISOString()
            });
        });
    }

    /**
     * Send NX Agent message via WebSocket (Direct Mode)
     */
    sendNxAgentMessage(payload: {
        userMessage: string;
        history?: string;
        companymId: string;
        userRole: string;
        userId: string;
        conversationId?: string;
        activeProjectId?: string;
        fileSessions?: Array<{
            fileId: string;
            fileName: string;
            fileType: string;
            processedContent?: string;
            base64Content?: string;
            imageUrl?: string;
            metadata: any;
            tokenEstimate: number;
            uploadedAt: Date;
        }>;
        workflowMode?: 'auto' | 'nxopen' | 'nxhelp' | 'documents';
        planMode?: boolean;
        onlineSearchEnabled?: boolean;
        serverModelingEnabled?: boolean;
        reasoningEffort?: string;
        modelSelection?: {
            profileId: string;
            model: string;
        } | null;
        successfulCaseMinScore?: number;
        furtherSearchAll?: boolean;
        regeneration?: {
            enabled: true;
            targetUserMessageIndex: number;
        };
    }): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.socket || !this.isConnected) {
                reject(new Error('WebSocket not connected'));
                return;
            }

            console.log('[WebSocketService] Sending NX Agent message via WebSocket:', payload);

            // Send NX agent message (streaming response will be handled via nx-agent-stream event)
            this.socket.emit('nx-agent-message', {
                userMessage: payload.userMessage,
                history: payload.history,
                companymId: payload.companymId,
                userRole: payload.userRole,
                userId: payload.userId,
                conversationId: payload.conversationId,
                activeProjectId: payload.activeProjectId,
                timestamp: new Date().toISOString(),
                fileSessions: payload.fileSessions,
                workflowMode: payload.workflowMode || 'auto',
                planMode: payload.planMode || false,
                onlineSearchEnabled: payload.onlineSearchEnabled || false,
                serverModelingEnabled: payload.serverModelingEnabled === true,
                reasoningEffort: payload.reasoningEffort || 'default',
                modelSelection: payload.modelSelection,
                successfulCaseMinScore: payload.successfulCaseMinScore,
                furtherSearchAll: payload.furtherSearchAll === true,
                regeneration: payload.regeneration
            });

            // Resolve immediately since we handle the response via stream events
            resolve();
        });
    }

    /**
     * Subscribe to NX agent streamed messages
     */
    onNxAgentStream(handler: (payload: { role: string; content: string; node?: string; type?: string; toolName?: string; toolId?: string; toolStatus?: 'success' | 'error'; aborted?: boolean; conversationId?: string, classificationData?: any; sources?: any[]; projectTableData?: any; finalNode?: string | null; finalNodeContent?: string; resultImages?: Array<{ mimeType: string; dataUri: string }> }) => void) {
        this.nxAgentStreamHandlers.push(handler);
        return () => {
            this.nxAgentStreamHandlers = this.nxAgentStreamHandlers.filter(h => h !== handler);
        };
    }

    /**
     * Releases request-scoped NX stream subscribers after transport loss or recovered completion.
     * This prevents late events from an old request being rendered by the next auto-test prompt.
     */
    clearNxAgentStreamHandlers(reason: string): void {
        const handlers = [...this.nxAgentStreamHandlers];
        handlers.forEach(handler => {
            handler({
                role: 'assistant',
                content: reason,
                type: 'stream_disconnect'
            });
        });
        this.nxAgentStreamHandlers = [];
    }

    /**
     * Register the handler that renders an editable plan card and resolves with the
     * user-confirmed plan text. Only one handler is supported (last registration wins).
     */
    onNxAgentPlan(handler: (plan: NxAgentPlanData) => Promise<string>): () => void {
        this.nxAgentPlanHandler = handler;
        return () => {
            if (this.nxAgentPlanHandler === handler) {
                this.nxAgentPlanHandler = null;
            }
        };
    }

    /**
     * Register the handler that renders a question card and resolves with the
     * user's answer string. Only one handler is supported (last registration wins).
     */
    onNxAgentQuestion(handler: (question: NxAgentQuestionData) => Promise<string>): () => void {
        this.nxAgentQuestionHandler = handler;
        return () => {
            if (this.nxAgentQuestionHandler === handler) {
                this.nxAgentQuestionHandler = null;
            }
        };
    }

    /**
     * Register the handler that confirms execute_python_code before NX runs it.
     * Only one handler is supported (last registration wins).
     */
    onNxCodeExecutionApproval(handler: (request: NxCodeExecutionApprovalRequest) => Promise<boolean>): () => void {
        this.nxCodeExecutionApprovalHandler = handler;
        return () => {
            if (this.nxCodeExecutionApprovalHandler === handler) {
                this.nxCodeExecutionApprovalHandler = null;
            }
        };
    }

    private cancelPendingCodeExecutionApproval(): void {
        const cancel = this.pendingCodeApprovalCancel;
        this.pendingCodeApprovalCancel = null;
        if (cancel) {
            window.dispatchEvent(new CustomEvent('nx-code-execution-approval-cancelled'));
            cancel();
        }
    }

    private awaitCodeExecutionApproval(request: NxCodeExecutionApprovalRequest): Promise<boolean> {
        const handler = this.nxCodeExecutionApprovalHandler;
        if (!handler) return Promise.resolve(true);

        return new Promise<boolean>((resolve) => {
            let settled = false;
            const settle = (approved: boolean) => {
                if (settled) return;
                settled = true;
                this.pendingCodeApprovalCancel = null;
                resolve(approved);
            };
            this.pendingCodeApprovalCancel = () => settle(false);
            handler(request).then(settle, () => settle(false));
        });
    }

    /**
     * Subscribe to Regular RAG streamed messages
     */
    onRagStream(handler: (payload: any) => void): () => void {
        this.ragStreamHandlers.push(handler);
        return () => {
            const index = this.ragStreamHandlers.indexOf(handler);
            if (index > -1) {
                this.ragStreamHandlers.splice(index, 1);
            }
        };
    }

    /**
     * Send Regular RAG streaming message
     */
    sendRagStreamMessage(payload: {
        message: string;
        conversationId?: string;
        activeProjectId?: string;
        companyMId: string;
        userRole: string;
        userId: string;
        chatHistory?: any[];
    }): void {
        if (!this.socket || !this.isConnected) {
            console.error('[WebSocketService] Cannot send RAG stream message: not connected');
            return;
        }

        console.log('[WebSocketService] Sending RAG stream message via WebSocket');

        this.socket.emit('rag-stream-message', payload);
    }

    /**
     * Abort Regular RAG stream
     */
    abortRagStream(): void {
        if (!this.socket || !this.isConnected) {
            console.error('[WebSocketService] Cannot abort RAG stream: not connected');
            return;
        }

        console.log('[WebSocketService] 🛑 Aborting RAG stream');
        this.socket.emit('rag-stream-abort', {});
    }

    /**
     * Check if WebSocket is connected
     */
    isSocketConnected(): boolean {
        return this.isConnected
            && this.socket?.connected === true
            && this.nxSnippetRouteReady;
    }

    /**
     * Get socket instance for custom event handling
     */
    getSocket(): Socket | null {
        return this.socket;
    }

    /**
     * Abort current NX Agent WebSocket streaming
     * This sends a stop signal to the server to halt ongoing operations
     */
    abortNxAgentStream(): boolean {
        if (!this.socket || !this.isConnected) {
            console.warn('[WebSocket] Cannot abort NX Agent stream - WebSocket not connected');
            return false;
        }

        try {
            console.log('[WebSocket] 🛑 Aborting NX Agent stream...');
            this.cancelPendingCodeExecutionApproval();

            // Send abort signal to server
            this.socket.emit('nx-agent-abort', {
                timestamp: new Date().toISOString(),
                reason: 'user_requested_stop'
            });

            // Clear any pending stream handlers for this session
            console.log('[WebSocket] ✅ NX Agent stream abort signal sent');
            return true;
        } catch (error) {
            console.error('[WebSocket] ❌ Error aborting NX Agent stream:', error);
            return false;
        }
    }

    /**
     * Abort current RAG WebSocket streaming (legacy HTTP-based abort)
     * This sends a stop signal to the server to halt ongoing RAG operations
     * Note: This is for the old HTTP-based RAG, streaming uses abortRagStream above
     */
    abortRagStreamLegacy(): boolean {
        if (!this.socket || !this.isConnected) {
            console.warn('[WebSocket] Cannot abort RAG stream - WebSocket not connected');
            return false;
        }

        try {
            console.log('[WebSocket] 🛑 Aborting RAG stream (legacy)...');

            // Send abort signal to server
            this.socket.emit('rag-abort', {
                timestamp: new Date().toISOString(),
                reason: 'user_requested_stop'
            });

            console.log('[WebSocket] ✅ RAG stream abort signal sent');
            return true;
        } catch (error) {
            console.error('[WebSocket] ❌ Error aborting RAG stream:', error);
            return false;
        }
    }

    /**
     * Send NX command analysis request (streaming)
     */
    sendNXCommandAnalysis(payload: {
        chatContent: string;
        language: string;
        conversationId: string | null;
        userId: string;
        companyMId: string;
        clientId: string;
    }): void {
        if (this.socket?.connected) {
            console.log('[WebSocket] 🔍 Sending NX command analysis request');
            this.socket.emit('nx-command-analysis', payload);
        } else {
            console.error('[WebSocket] Cannot send NX analysis: Socket not connected');
        }
    }

    /**
     * Listen for NX command analysis stream
     */
    onNXCommandStream(callback: (data: {
        type: string;
        content?: string;
        commands?: any[];
        keyTerms?: string[];
        conversationId?: string;
        error?: string;
    }) => void): () => void {
        if (!this.socket) return () => { };

        console.log('[WebSocket] 📡 Subscribing to NX command stream');
        this.socket.on('nx-command-stream', callback);

        return () => {
            if (this.socket) {
                console.log('[WebSocket] 📡 Unsubscribing from NX command stream');
                this.socket.off('nx-command-stream', callback);
            }
        };
    }

    /**
     * Abort NX command analysis stream
     */
    abortNXCommandAnalysis(clientId: string): void {
        if (this.socket?.connected) {
            console.log('[WebSocket] 🛑 Aborting NX command analysis:', clientId);
            this.socket.emit('nx-command-abort', { clientId });
        }
    }

    // ===========================
    // NXRAG BACKGROUND JOB EVENTS
    // ===========================

    /**
     * Subscribe to NXRAG background job progress updates
     * @param callback Callback function to receive job progress updates
     * @returns Cleanup function to unsubscribe
     */
    onNxragJobProgress(callback: (data: {
        jobId: string;
        status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
        nxapp: string;
        totalProgress: number;
        files: Array<{
            filename: string;
            fileType: string;
            status: string;
            progress: number;
            error?: string;
        }>;
        results: {
            definitions: number;
            customerDefaults: number;
            help: number;
            commands: number;
            relationships: number;
            sections: number;
            errors: string[];
        };
        createdAt: string;
        startedAt?: string;
        completedAt?: string;
    }) => void): () => void {
        if (!this.socket) {
            console.warn('[WebSocket] ⚠️ Socket not connected, cannot subscribe to NXRAG job progress');
            return () => { };
        }

        console.log('[WebSocket] 📡 Subscribing to NXRAG job progress updates');
        this.socket.on('nxrag-job-progress', callback);

        return () => {
            if (this.socket) {
                console.log('[WebSocket] 📡 Unsubscribing from NXRAG job progress updates');
                this.socket.off('nxrag-job-progress', callback);
            }
        };
    }

    // ===========================
    // UPLOAD PROGRESS TRACKING
    // ===========================

    /**
     * Authenticate socket for progress tracking
     * Call this after connect() to enable progress updates
     */
    authenticateForProgress(userId: string, companyId: string): void {
        console.log(`[WebSocket] 🔐 authenticateForProgress called - userId: ${userId}, companyId: ${companyId}`);
        console.log(`[WebSocket] 🔐 Socket state - socket: ${!!this.socket}, isConnected: ${this.isConnected}, socket.connected: ${this.socket?.connected}`);

        if (!this.socket || !this.isConnected) {
            console.warn('[WebSocket] Cannot authenticate for progress: not connected');
            return;
        }

        const token = authService.getToken();
        console.log(`[WebSocket] 🔐 Emitting authenticate event with token: ${token ? 'present' : 'missing'}`);
        this.socket.emit('authenticate', { token, userId, companyId });
        console.log('[WebSocket] 🔐 authenticate event emitted successfully');
    }

    /**
     * Subscribe to task progress updates
     * @param taskId The task ID to subscribe to
     */
    subscribeProgress(taskId: string): void {
        if (!this.socket || !this.isConnected) {
            console.warn('[WebSocket] Cannot subscribe to progress: not connected');
            return;
        }

        console.log(`[WebSocket] 📡 Subscribing to progress for task: ${taskId}`);
        this.socket.emit('subscribe_progress', taskId);
    }

    /**
     * Unsubscribe from task progress updates
     * @param taskId The task ID to unsubscribe from
     */
    unsubscribeProgress(taskId: string): void {
        if (!this.socket) return;

        console.log(`[WebSocket] 📡 Unsubscribing from progress for task: ${taskId}`);
        this.socket.emit('unsubscribe_progress', taskId);
    }

    /**
     * Listen for progress update events
     * @param callback Callback function to receive progress updates
     * @returns Cleanup function to unsubscribe
     */
    onProgressUpdate(callback: (data: ProgressUpdateData) => void): () => void {
        this.progressHandlers.add(callback);

        return () => {
            this.progressHandlers.delete(callback);
        };
    }

    /**
     * Subscribe to OCR job updates
     * @param jobId The OCR job ID to subscribe to
     */
    subscribeOcrJob(jobId: string): void {
        if (!this.socket || !this.isConnected) {
            console.warn('[WebSocket] Cannot subscribe to OCR job: not connected');
            return;
        }

        console.log(`[WebSocket] 📡 Subscribing to OCR job: ${jobId}`);
        this.socket.emit('subscribe_ocr_job', jobId);
    }

    /**
     * Unsubscribe from OCR job updates
     * @param jobId The OCR job ID to unsubscribe from
     */
    unsubscribeOcrJob(jobId: string): void {
        if (!this.socket) return;

        console.log(`[WebSocket] 📡 Unsubscribing from OCR job: ${jobId}`);
        this.socket.emit('unsubscribe_ocr_job', jobId);
    }

    /**
     * Listen for OCR job status events
     * @param callback Callback function to receive OCR job status
     * @returns Cleanup function to unsubscribe
     */
    onOcrJobStatus(callback: (data: OcrJobStatusData) => void): () => void {
        this.ocrJobStatusHandlers.add(callback);

        return () => {
            this.ocrJobStatusHandlers.delete(callback);
        };
    }

    /**
     * Listen for OCR job progress events
     * @param callback Callback function to receive OCR job progress
     * @returns Cleanup function to unsubscribe
     */
    onOcrJobProgress(callback: (data: OcrJobProgressData) => void): () => void {
        this.ocrJobProgressHandlers.add(callback);

        return () => {
            this.ocrJobProgressHandlers.delete(callback);
        };
    }

    /**
     * Listen for NX Training background embedding progress events.
     * The server pushes these to the account's company room, so the caller must
     * have authenticated for progress (authenticateForProgress) after connecting.
     * @param callback Callback function to receive embedding progress/result
     * @returns Cleanup function to unsubscribe
     */
    onNxEmbeddingProgress(callback: (data: NxEmbeddingProgressData) => void): () => void {
        this.nxEmbeddingHandlers.add(callback);

        return () => {
            this.nxEmbeddingHandlers.delete(callback);
        };
    }
}

// Export singleton instance
export const webSocketService = new WebSocketService();
export default webSocketService;
